import * as React from "react";
import { TextField, Button, Snackbar, Alert, CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getUser, login } from "../../../Redux/Auth/Action";
import PasswordField from "./PasswordField";
import { useEffect } from "react";
import { useState, useRef } from "react";

// Sign-in validation is deliberately LIGHTER than sign-up's.
//
// Sign-up decides what may enter the database; sign-in must let every EXISTING
// account back in. Applying sign-up's rules here would lock out anyone whose
// account predates them — e.g. a password shorter than 6 characters, which was
// allowed until recently and which bcrypt makes impossible to detect. So this
// only checks that something was entered, and lets the API judge the rest.
export default function LoginUserForm({ setPageType }) {
  const dispatch = useDispatch();
  const jwt = localStorage.getItem("jwt");
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const { auth } = useSelector((store) => store);
  const handleCloseSnakbar = () => setOpenSnackBar(false);
  // Local flag rather than auth.isLoading: that flag is also raised by getUser,
  // which runs on mount, so the button would spin before anyone submitted.
  const [submitting, setSubmitting] = useState(false);
  // A ref is what actually stops a double submit — setSubmitting is async, so
  // two fast clicks both read the old value and both fire (each one costs a
  // request against the 20-per-15-min auth rate limit).
  const inFlight = useRef(false);
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});

  const clearError = (name) =>
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));

  useEffect(() => {
    if (jwt) {
      dispatch(getUser(jwt));
    }
  }, [dispatch, jwt]);

  useEffect(() => {
    if (auth.user || auth.error) setOpenSnackBar(true);
  }, [auth.user, auth.error]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (inFlight.current) return;

    // Presence only — NOT a format or length check. See the note above.
    const found = {};
    if (!identifier.trim()) found.identifier = "Enter your email or mobile number.";
    if (!password) found.password = "Enter your password.";
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    const userData = {
      // Either an email address or a mobile number — the API resolves which.
      // Trimmed so a copy-pasted address with a trailing space still matches.
      identifier: identifier.trim(),
      password,
    };
    inFlight.current = true;
    setSubmitting(true);
    await dispatch(login(userData));
    // On success the modal closes / redirects, but reset anyway so a failed
    // attempt leaves the form usable.
    inFlight.current = false;
    setSubmitting(false);
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-display text-3xl font-bold text-[#511d00]">
          Welcome Back
        </h2>
        <p className="mt-1 text-sm text-[#8a7575]">
          Sign in to continue to your account.
        </p>
      </div>

      <form className="w-full" onSubmit={handleSubmit}>
        <div className="space-y-4">
          <TextField
            id="identifier"
            name="identifier"
            label="Email or Mobile Number"
            autoComplete="username"
            fullWidth
            required
            value={identifier}
            onChange={(e) => {
              setIdentifier(e.target.value);
              clearError("identifier");
            }}
            error={Boolean(errors.identifier)}
            helperText={errors.identifier}
          />
          <PasswordField
            id="password"
            name="password"
            label="Password"
            fullWidth
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              clearError("password");
            }}
            error={Boolean(errors.password)}
            helperText={errors.password}
          />
          <div className="-mt-2 flex justify-end">
            <button
              type="button"
              onClick={() => setPageType("forgot")}
              className="text-xs font-semibold text-[#b28585] hover:underline"
            >
              Forgot password?
            </button>
          </div>
          <Button
            className="w-full"
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={submitting}
            sx={{ py: 1.3, borderRadius: 2, fontWeight: 700 }}
          >
            {submitting ? (
              <CircularProgress size={22} sx={{ color: "#fff" }} />
            ) : (
              "Sign In"
            )}
          </Button>
        </div>
      </form>

      <div className="mt-5 flex items-center justify-center gap-1 text-sm text-[#5e3e3e]">
        <span>Don't have an account?</span>
        <button
          onClick={() => setPageType("register")}
          className="font-semibold text-[#b28585] underline-offset-2 hover:underline"
        >
          Sign Up
        </button>
      </div>

      <Snackbar
        open={openSnackBar}
        autoHideDuration={6000}
        onClose={handleCloseSnakbar}
      >
        <Alert
          onClose={handleCloseSnakbar}
          severity={auth.error ? "error" : "success"}
          sx={{ width: "100%" }}
        >
          {auth.error ? auth.error : auth.user ? "Login Success" : ""}
        </Alert>
      </Snackbar>
    </div>
  );
}
